import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { subDays } from 'date-fns';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type AnnouncementModel from 'garaje/models/announcement';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';

import { getAnnouncementQueryParams } from './announcement-queries';
import type CommunicationsMessageLogListControllerBase from './controller-base';
import { fetchIncidentsByAnnouncementId, type IncidentSummary } from './incident-queries';

export const PAGE_SIZE = 25;
const SIX_MONTHS_IN_DAYS = 180;

export type AnnoucementsListModel = {
  announcements: PaginatedRecordArray<AnnouncementModel>;
  startDate: Date;
  endDate: Date;
  incidentsByAnnouncementId: Map<string, IncidentSummary>;
};

export default class CommunicationsMessageLogRouteBase extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;

  async model(): Promise<AnnoucementsListModel | void> {
    const currentDate = new Date();
    const startDate = subDays(currentDate, SIX_MONTHS_IN_DAYS);
    const endDate = currentDate;
    const locationId = this.currentLocation?.location?.id;

    const flagEnabled = this.featureFlags.isEnabled('emno-end-incident-web');

    const [announcements, incidentsByAnnouncementId] = await Promise.all([
      this.loadAnnouncements(startDate, endDate),
      flagEnabled && locationId
        ? fetchIncidentsByAnnouncementId(locationId, startDate, endDate)
        : Promise.resolve(new Map<string, IncidentSummary>()),
    ]);

    return { announcements, startDate, endDate, incidentsByAnnouncementId };
  }

  async loadAnnouncements(startDate: Date, endDate: Date): Promise<PaginatedRecordArray<AnnouncementModel>> {
    const queryParams = getAnnouncementQueryParams([this.currentLocation?.location?.id], startDate, endDate, PAGE_SIZE);

    try {
      const result = await this.store.query('announcement', queryParams);
      return result as PaginatedRecordArray<AnnouncementModel>;
    } catch (_error) {
      throw new Error(`Failed to load announcements`);
    }
  }

  setupController(
    controller: CommunicationsMessageLogListControllerBase,
    model: AnnoucementsListModel,
    transition: Transition,
    loadLocations: boolean = true,
  ): void {
    const { startDate, endDate } = model;
    super.setupController(controller, model, transition);
    controller.hydrateFromModel(model);
    void controller.initializeData.perform(startDate, endDate, loadLocations);
  }
}
